import clsx from 'clsx';
import { CustomMenu } from '../../menus/custom-menu';
import { LocaleSwitcher } from '@common/locale-switcher/locale-switcher';
import { Button } from '@ui/buttons/button';
import { DarkModeIcon } from '@ui/icons/material/DarkMode';
import { LightbulbIcon } from '@ui/icons/material/Lightbulb';
import { Trans } from '@ui/i18n/trans';
import { useThemeSelector } from '@ui/themes/theme-selector-context';
import { useSettings } from '@ui/settings/use-settings';
import { Link } from 'react-router-dom';
import { useAppearanceStore } from '@common/admin/appearance/appearance-store';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useFooterPages } from '@common/admin/settings/pages/push-notification-send';

interface Props {
  className?: string;
  padding?: string;
}

export function Footer({ className, padding }: Props) {
  const year = new Date().getFullYear();
  const { branding } = useSettings();

  const { data } = useFooterPages();

  const footerLinks = data ?? [];

  const primaryFooterLinks = footerLinks.slice(0, 4);
  const overflowFooterLinks = footerLinks.slice(4);

  return (
    <section className='footer_bg'>
      <footer
        className='landing-container'
      >
        <div className='row'>
          {/* Get App Column */}
          {(branding.apk_url || branding.pc_url) && (
            <div className='col-xl-33 col-lg-4 col-md-4 col-sm-6 col-6 mb-4'>
              <div className='footer_contents'>
                <h3>Get App</h3>
                {branding.pc_url && (
                  <a href={branding.pc_url} target="_blank" rel="noreferrer">
                    <p>One time for pc</p>
                  </a>
                )}
                {branding.apk_url && (
                  <a href={branding.apk_url} target="_blank" rel="noreferrer">
                    <p>One time APK</p>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Follow Us Column */}
          {(branding.facebook_url || branding.youtube_url || branding.twitter_url) && (
            <div className='col-xl-33 col-lg-4 col-md-4 col-sm-6 col-6 mb-4'>
              <div className='footer_contents'>
                <h3>Follow Us</h3>
                {branding.facebook_url && (
                  <a href={branding.facebook_url} target="_blank" rel="noreferrer">
                    <p>Facebook</p>
                  </a>
                )}
                {branding.youtube_url && (
                  <a href={branding.youtube_url} target="_blank" rel="noreferrer">
                    <p>Youtube</p>
                  </a>
                )}
                {branding.twitter_url && (
                  <a href={branding.twitter_url} target="_blank" rel="noreferrer">
                    <p>Twitter</p>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Primary Pages Column - Displays the first 4 pages if available */}
          {primaryFooterLinks.length > 0 && (
            <div className="col-xl-33 col-lg-4 col-md-4 col-sm-6 col-6 mb-4">
              <div className="footer_contents">
                <h3>Legal Pages</h3>
                {primaryFooterLinks.map((page) => (
                  <p key={page.slug}>
                    <Link to={`/pages/${page.slug}`}>{page.title}</Link>
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Overflow Pages Column - Only rendered if there are more than 4 links */}
          {overflowFooterLinks.length > 0 && (
            <div className="col-xl-33 col-lg-4 col-md-4 col-sm-6 col-6 mb-4">
              <div className="footer_contents">
                {/* A slightly different title to indicate continuation of pages */}
                <h3>More Legal Pages</h3>
                {overflowFooterLinks.map((page) => (
                  <p key={page.slug}>
                    <Link to={`/pages/${page.slug}`}>{page.title}</Link>
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        <Menus />
        <div className="items-center justify-between gap-30 text-center text-muted md:flex md:text-left">
          <Trans
            message="Copyright © :year :name, All Rights Reserved"
            values={{ year, name: branding.site_name }}
          />
          <div>
            <LocaleSwitcher />
          </div>
        </div>
      </footer>
    </section>
  );
}

function Menus() {
  const settings = useSettings();
  const primaryMenu = settings.menus.find(m => m.positions?.includes('footer'));
  const secondaryMenu = settings.menus.find(
    m => m.positions?.includes('footer-secondary'),
  );

  if (!primaryMenu && !secondaryMenu) return null;
}

function ThemeSwitcher() {
  const { themes } = useSettings();
  const { selectedTheme, selectTheme } = useThemeSelector();
  if (!selectedTheme || !themes?.user_change) return null;

  return (
    <Button
      variant="text"
      startIcon={selectedTheme.is_dark ? <DarkModeIcon /> : <LightbulbIcon />}
      onClick={() => {
        if (selectedTheme.is_dark) {
          selectTheme('light');
        } else {
          selectTheme('dark');
        }
      }}
    >
      {selectedTheme.is_dark ? (
        <Trans message="Light mode" />
      ) : (
        <Trans message="Dark mode" />
      )}
    </Button>
  );
}
